import clsx from "clsx";
import { TFunction } from "i18next";
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Close, ListSearchIcon } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { ListBoxMultiple } from "@/components/Interface/FormFields/ListBoxMultiple/ListBoxMultiple";
import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import { TableControlGroup } from "@/components/Layout/Table/TabeControlGroup/TableControlGroup";
import { Table } from "@/components/Layout/Table/Table";
import { useHandleError } from "@/hooks/useHandleError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQueryParam } from "@/hooks/useQueryParam";
import {
  getEvnFiles,
  useGetEvnDevices,
  useGetEvnForCustomer,
} from "@/services/api";
import { DrEvn, EvnFile } from "@/services/model";
import { FCC } from "@/types";
import { log } from "@/utils/log";

import { ItemizedBillOverviewHeader } from "./ItemizedBillOverviewHeader";
import { ItemizedBillOverviewRow } from "./ItemizedBillOverviewRow";

export type ItemizedBillListIdForm = {
  selectedItems: string[]; // stores encoded values
};

type FilterFormDevice = {
  deviceList: string[];
};

type FilterFormPeriod = {
  periods: string[];
};

const DropDownLabel: FCC = ({ children }) => (
  <span className="mb-4 text-lg font-semibold text-secondary-100">
    {children}
  </span>
);

/** Headless UI focuses `Listbox.Options` on open; refocus this field and stop keys bubbling to listbox typeahead. */
const EvnDeviceLineComboHeader = ({
  open,
  deviceComboFilter,
  setDeviceComboFilter,
  inputRef,
}: {
  open: boolean;
  deviceComboFilter: string;
  setDeviceComboFilter: (value: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
}) => {
  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const id = window.setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(id);
  }, [open, inputRef]);

  const clearFilter = () => {
    setDeviceComboFilter("");
    window.queueMicrotask(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
  };

  return (
    <div
      className="border-b border-primary-100 bg-white p-3"
      onMouseDown={(e) => e.preventDefault()}
    >
      <label className="sr-only" htmlFor="evn-device-combo-filter">
        {t("documents.tabItemizedBill.placeholderSearchTerm")}
      </label>
      <div className="flex min-w-0 items-center gap-2">
        <input
          ref={inputRef as RefObject<HTMLInputElement>}
          id="evn-device-combo-filter"
          type="text"
          inputMode="search"
          autoComplete="off"
          data-testid="evn-device-combo-filter-input"
          value={deviceComboFilter}
          onChange={(e) => setDeviceComboFilter(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder={t("documents.tabItemizedBill.placeholderSearchTerm")}
          className="min-w-0 flex-1 border-0 bg-transparent text-base text-primary-100 outline-none ring-0 placeholder:text-gray-500"
        />
        {deviceComboFilter ? (
          <button
            type="button"
            className="flex shrink-0 items-center justify-center rounded p-1 text-primary-100 hover:bg-primary-25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-100"
            aria-label={t("documents.tabItemizedBill.clearLineFilter")}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              clearFilter();
            }}
          >
            <Close aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const assignFileToAnchor = (
  data: EvnFile,
  elem: HTMLAnchorElement,
  t: TFunction<"portal", undefined, "portal">,
): HTMLAnchorElement | undefined => {
  if (data.evnFile?.file && data.evnFile.mimeType === "application/pdf") {
    const filename = `DR_EVN-${new Date()
      .toISOString()
      .replaceAll(/:|\./g, "-")}.pdf`;
    elem.setAttribute("target", "_blank");
    elem.setAttribute("download", filename);

    // On chrome, we cannot open data urls, so we convert to a blob and create it this way
    if (navigator.userAgent.includes("Firefox")) {
      elem.setAttribute(
        "href",
        `data:application/pdf;base64,${data.evnFile?.file}`,
      );
    } else {
      // decode base64 string, remove space for IE compatibility
      const binary = atob(
        (data.evnFile.file as unknown as string).replace(/\s/g, ""),
      );
      const len = binary.length;
      const buffer = new ArrayBuffer(len);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
      }

      // create the blob object with content-type "application/pdf"
      const blob = new Blob([view], { type: "application/pdf" });
      elem.setAttribute("href", window.URL.createObjectURL(blob));
    }

    return elem;
  } else if (data.evnFile?.mimeType === "application/pdf") {
    const url = `data:application/zip;base64,${data.evnFile?.file}`;
    const link = document.createElement("a");

    link.download = t("documents.tabItemizedBill.zipName");
    link.href = url;
    link.click();
  } else {
    log("Could not save bill pdf, the check for valid object failed.");
  }
};

export const ItemizedBillOverview = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const isMobile = useIsMobile();

  const [shouldShowEntries, setShouldShowEntries] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [deviceComboFilter, setDeviceComboFilter] = useState("");
  const evnDeviceComboInputRef = useRef<HTMLInputElement>(null);
  const refocusEvnDeviceComboFilter = useCallback(() => {
    window.queueMicrotask(() => {
      evnDeviceComboInputRef.current?.focus({ preventScroll: true });
    });
  }, []);
  const [availablePeriods, setAvailablePeriods] =
    useState<(string | undefined)[]>();
  const [isSelectMultiple, setIsSelectMultiple] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { watch: watchDevice, control: controlDevice } =
    useForm<FilterFormDevice>({
      defaultValues: {},
    });
  const formStateDevice = watchDevice();

  const { watch: watchPeriod, control: controlPeriod } =
    useForm<FilterFormPeriod>({
      defaultValues: {},
    });
  const formStatePeriod = watchPeriod();
  const hasSelectedDeviceFilter =
    (formStateDevice?.deviceList?.length ?? 0) > 0;
  const hasSelectedPeriodFilter = (formStatePeriod?.periods?.length ?? 0) > 0;

  const { data: itemizedBillDevices, isFetching: isFetchingDevices } =
    useGetEvnDevices();

  const filteredPlanDeviceLabels = useMemo(() => {
    const labels = (itemizedBillDevices ?? [])
      .map((d) => d.device)
      .filter((d): d is string => Boolean(d));
    const q = deviceComboFilter.trim().toLowerCase().replace(/\s+/g, "");

    if (!q) {
      return labels;
    }

    return labels.filter((label) =>
      label.toLowerCase().replace(/\s+/g, "").includes(q),
    );
  }, [itemizedBillDevices, deviceComboFilter]);

  const [itemizedBillPage, setCurrentItemizedBillPage] = useQueryParam("page");
  const currentItemizedBillPage = Number(itemizedBillPage) || 1;
  const [pageSizeParam] = useQueryParam("pageSize");
  const currentPageSize = Number(pageSizeParam) || 10;

  /** Contract text search is not exposed in UI; expression matches previous `searchTerm?.trim() || undefined` when empty. */
  const contractSearchForApi = "";

  const { data: itemizedBillOverview, isFetching: isFetchingOverview } =
    useGetEvnForCustomer(
      {
        searchTermContract: contractSearchForApi.trim() || undefined,
        periods: formStatePeriod.periods,
        page: currentItemizedBillPage - 1,
        pageSize: currentPageSize,
      },
      {
        query: {
          refetchOnWindowFocus: false,
          enabled: shouldFetch,
          onSuccess: (data) => {
            setShouldFetch(false);

            setAvailablePeriods((prev) => [
              ...new Set([
                ...(prev ?? []),
                ...(data?.evns?.map((evn) => evn.periode) ?? []),
              ]),
            ]);

            // If the total number of pages is smaller than the current selected page,
            // (e.g. we are on page 3 but the data has only 2 pages now)
            // reset to the next possible value
            if (
              data.totalNumberPages &&
              data.totalNumberPages < currentItemizedBillPage
            ) {
              setCurrentItemizedBillPage(data.totalNumberPages.toString());
            }
          },
        },
      },
    );

  useEffect(() => {
    if (shouldShowEntries) {
      setShouldFetch(true);
    }
  }, [currentItemizedBillPage, currentPageSize, shouldShowEntries]);

  const hasItemizedBills = useMemo(
    () => itemizedBillOverview?.evns && itemizedBillOverview?.evns?.length >= 1,
    [itemizedBillOverview?.evns],
  );

  const requestedItemizedBills = useMemo<DrEvn[]>(() => {
    if (shouldShowEntries && !shouldFetch) {
      if (formStateDevice.deviceList && formStateDevice.deviceList.length > 0) {
        return (
          itemizedBillOverview?.evns?.filter((evn) =>
            formStateDevice.deviceList.includes(evn.contract || ""),
          ) || []
        );
      }

      return itemizedBillOverview?.evns ?? [];
    }

    return [];
  }, [
    formStateDevice.deviceList,
    shouldShowEntries,
    shouldFetch,
    itemizedBillOverview?.evns,
  ]);

  const sortedBills = useMemo(() => {
    if (!requestedItemizedBills) return [];

    const sorted = [...requestedItemizedBills];

    sorted.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case "PLAN":
          aVal = a.contract;
          bVal = b.contract;
          break;
        case "PERIOD":
          aVal = a.periode;
          bVal = b.periode;
          break;
        case "AMOUNT":
          aVal = a.amount;
          bVal = b.amount;
          break;
        case "NUMBER":
          aVal = a.billNumber;
          bVal = b.billNumber;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;

      return 0;
    });

    return sorted;
  }, [requestedItemizedBills, sortField, sortDirection]);

  const formMethods = useForm<ItemizedBillListIdForm>({
    defaultValues: { selectedItems: [] },
  });

  const selectedValues = formMethods.watch("selectedItems") ?? [];

  const selectedItems = selectedValues.map((value) => {
    const [serviceId, period] = value.split("|");

    return {
      serviceId: Number(serviceId),
      period,
    };
  });

  const downloadItemizedBill = ({
    serviceId,
    period,
  }: {
    serviceId: number;
    period: string;
  }) => {
    setIsFetchingFile(true);

    getEvnFiles({ serviceIds: [serviceId], periods: [period] })
      .then((data) => {
        let elem: HTMLAnchorElement | undefined =
          window.document.createElement("a");
        elem = assignFileToAnchor(data, elem, t);

        if (elem) {
          elem.click();

          // cleanup (important for Safari)
          setTimeout(() => {
            window.URL.revokeObjectURL(elem.href);
            elem.remove();
          }, 100);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const downloadSelectedItemizedBills = () => {
    setIsFetchingFile(true);

    getEvnFiles({
      serviceIds: selectedItems.map((item) => item.serviceId),
      periods: selectedItems.map((item) => item.period),
    })
      .then((data) => {
        if (!data.evnFile?.file || !data.evnFile.mimeType) {
          log("Invalid file response");

          return;
        }

        // Case 1: multiple files -> ZIP
        if (data.evnFile.mimeType === "application/zip") {
          const url = `data:application/zip;base64,${data.evnFile.file}`;
          const link = document.createElement("a");

          link.download = t("documents.tabItemizedBill.zipName");
          link.href = url;
          link.click();

          return;
        }

        // Case 2: single file -> PDF
        if (data.evnFile.mimeType === "application/pdf") {
          const elem = assignFileToAnchor(data, document.createElement("a"), t);

          if (elem) {
            document.body.appendChild(elem);
            elem.click();

            setTimeout(() => {
              window.URL.revokeObjectURL(elem.href);
              elem.remove();
            }, 100);
          }

          return;
        }

        log("Unsupported mime type:", data.evnFile.mimeType);
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingFile(false));
  };

  const itemizedBillPages = itemizedBillOverview?.totalNumberPages || 1;

  const Info = () => (
    <>
      <ListSearchIcon />
      <p className="my-6 text-xl font-semibold text-primary-100">
        {hasItemizedBills
          ? t("documents.tabItemizedBill.choosePlanAndPeriod")
          : t("documents.tabItemizedBill.noItemizedBillOverview")}
      </p>
      {!shouldShowEntries && hasItemizedBills && (
        <Button
          className="inverted accent text-sm md:w-auto md:text-lg"
          fullWidth={isMobile}
          onClick={() => {
            setShouldShowEntries(true);
            setCurrentItemizedBillPage(undefined);
          }}
        >
          {t("documents.tabItemizedBill.showAll")}
        </Button>
      )}
    </>
  );

  const MobileView = () => (
    <>
      <div className="flex flex-col">
        <button
          className={clsx(
            "self-end text-base font-semibold text-primary-100 underline",
            isSelectMultiple ? "opacity-50" : "opacity-100",
          )}
          onClick={() => {
            setIsSelectMultiple((prev) => !prev);

            if (!isSelectMultiple) {
              formMethods.resetField("selectedItems");
            }
          }}
        >
          {isSelectMultiple
            ? t("common.buttons.cancel")
            : t("bills.selectMultiple")}
        </button>

        <div className="flex flex-col">
          {sortedBills?.map((itemizedBill) => (
            <ItemizedBillOverviewRow
              key={itemizedBill.id}
              itemizedBill={itemizedBill}
              variant="compact"
              isSelectMultiple={isSelectMultiple}
              downloadItemizedBill={downloadItemizedBill}
            />
          ))}
        </div>
      </div>
      {shouldShowEntries && (
        <TableControlGroup>
          <div className="flex w-full flex-col gap-4">
            <Pagination paginationPages={itemizedBillPages} />
            {isSelectMultiple && (
              <Button
                className="accent"
                disabled={Object.values(selectedValues).length === 0}
                onClick={downloadSelectedItemizedBills}
                fullWidth
              >
                {t("bills.download", { count: selectedValues.length })}
              </Button>
            )}
          </div>
        </TableControlGroup>
      )}
    </>
  );

  const DesktopView = () => (
    <div className="w-full overflow-x-auto">
      <div className="min-w-max">
        <Table paginationPages={itemizedBillPages}>
          <thead>
            <ItemizedBillOverviewHeader
              currentPageItemizedBillIds={
                sortedBills
                  ?.filter(
                    (itemizedBill) =>
                      itemizedBill.serviceId && itemizedBill.periode,
                  )
                  .map((itemizedBill) => ({
                    serviceId: itemizedBill.serviceId!,
                    period: itemizedBill.periode!,
                  })) ?? []
              }
              onSortChange={(field, direction) => {
                setSortField(field);
                setSortDirection(direction);
              }}
            />
          </thead>
          <tbody>
            {sortedBills?.map((itemizedBill) => (
              <ItemizedBillOverviewRow
                key={itemizedBill.id}
                itemizedBill={itemizedBill}
                downloadItemizedBill={downloadItemizedBill}
              />
            ))}
          </tbody>
        </Table>
      </div>
      {shouldShowEntries && (
        <TableControlGroup>
          <div className="mr-8">
            <PageNumberComboBox />
          </div>
          {((isMobile && isSelectMultiple) || !isMobile) && (
            <Button
              className="accent"
              disabled={Object.values(selectedValues).length === 0}
              onClick={downloadSelectedItemizedBills}
            >
              {t("bills.download", { count: selectedValues.length })}
            </Button>
          )}
        </TableControlGroup>
      )}
    </div>
  );

  return (
    <div className="mt-10">
      {(isFetchingDevices || isFetchingOverview || isFetchingFile) && (
        <LoadingSpinner />
      )}
      <p>{t("documents.tabItemizedBill.subtitle")}</p>

      <div className="mt-10 flex w-full flex-col gap-6 md:flex-row">
        <div className="flex w-full flex-col">
          <DropDownLabel>
            {t("documents.tabItemizedBill.searchTermLabel")}
          </DropDownLabel>
          <Controller<FilterFormDevice>
            control={controlDevice}
            name="deviceList"
            render={({ field: { onChange, value } }) => (
              <ListBoxMultiple<string | undefined>
                className="full-width"
                itemLabel={t("documents.tabItemizedBill.searchTermLabel")}
                placeholder={t(
                  hasSelectedPeriodFilter
                    ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                    : "documents.tabItemizedBill.placeholderPlans",
                )}
                selected={typeof value === "string" ? [value] : value}
                setSelected={onChange}
                displayValue={(v) => v ?? ""}
                availableValues={filteredPlanDeviceLabels}
                disabled={
                  hasSelectedPeriodFilter || !itemizedBillDevices?.length
                }
                hideArrowWhenDisabled={hasSelectedPeriodFilter}
                showAmountForMultipleSelection
                afterSelectionChange={refocusEvnDeviceComboFilter}
                optionsHeader={({ open }) => (
                  <EvnDeviceLineComboHeader
                    open={open}
                    deviceComboFilter={deviceComboFilter}
                    setDeviceComboFilter={setDeviceComboFilter}
                    inputRef={evnDeviceComboInputRef}
                  />
                )}
              />
            )}
          />
        </div>

        <div className="flex w-full flex-col">
          <DropDownLabel>{t("documents.tabItemizedBill.period")}</DropDownLabel>
          <Controller<FilterFormPeriod>
            control={controlPeriod}
            name="periods"
            render={({ field: { onChange, value } }) => (
              <ListBoxMultiple<string | undefined>
                className="full-width"
                itemLabel={t("Content.periods")}
                placeholder={t(
                  hasSelectedDeviceFilter
                    ? "documents.tabItemizedBill.onlyOneFilterAllowed"
                    : "documents.tabItemizedBill.placeholderPeriods",
                )}
                selected={typeof value === "string" ? [value] : value}
                setSelected={onChange}
                displayValue={(value) => value ?? ""}
                availableValues={availablePeriods || []}
                disabled={
                  hasSelectedDeviceFilter ||
                  !availablePeriods ||
                  availablePeriods.length <= 0
                }
                hideArrowWhenDisabled={hasSelectedDeviceFilter}
                showAmountForMultipleSelection
              />
            )}
          />
        </div>

        <div className="flex items-end">
          <Button
            fullWidth
            className="accent w-full text-sm md:w-auto md:text-lg"
            onClick={() => {
              setShouldFetch(true);
              setShouldShowEntries(true);
              setCurrentItemizedBillPage(undefined);
            }}
          >
            {t("documents.tabItemizedBill.btnSearch")}
          </Button>
        </div>
      </div>

      <div className="mt-2 flex flex-col items-center py-10 text-center md:mt-0">
        <FormProvider {...formMethods}>
          {sortedBills?.length > 0 ? (
            isMobile ? (
              <MobileView />
            ) : (
              <DesktopView />
            )
          ) : (
            <Info />
          )}
        </FormProvider>
      </div>
    </div>
  );
};
